import React, { useState } from 'react';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Eye, EyeOff, Lock, Mail, ShieldAlert, Info } from 'lucide-react';

export default function Login() {
  const { portalLogin, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  // Wave 3.99ad — Reason banner shown when the user was force-signed-out by a
  // portal access policy (maintenance lockdown / off-hours / personal hours /
  // admin-ended session). The reason + Super-Admin-authored message are handed
  // over via sessionStorage by the axios interceptor (lib/api.js).
  const [lockout, setLockout] = useState(null); // { reason, message }
  React.useEffect(() => {
    try {
      const reason = sessionStorage.getItem('inv_lockout_reason');
      if (reason) {
        const message = sessionStorage.getItem('inv_lockout_message') || '';
        setLockout({ reason, message });
        sessionStorage.removeItem('inv_lockout_reason');
        sessionStorage.removeItem('inv_lockout_message');
      }
    } catch { /* noop */ }
  }, []);

  const LOCKOUT_FALLBACKS = {
    locked: 'The portal is currently unavailable. Please try again later.',
    offhours: 'The portal is open only during business hours (IST). Please sign in during the allowed hours.',
    user_offhours: 'You can sign in only during your allowed hours (IST). Please try again later.',
    session_ended: 'You’ve been signed out. Please sign in again.',
  };
  const LOCKOUT_TITLES = {
    locked: 'Portal under maintenance',
    offhours: 'Portal closed right now',
    user_offhours: 'Outside your allowed hours',
    session_ended: 'You’ve been signed out',
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg('');
    try {
      const res = await portalLogin(email, password);
      toast.success(`Welcome, ${res.user.name || res.user.email}`);
      // The submitted account decides where we land. Notes users open on the
      // Planner Daily view (today) by default; ERP users go to the dashboard.
      window.location.href = res.target === 'notes' ? '/notes/planner' : '/dashboard';
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Login failed. Please check your credentials.';
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6 py-12">
      <div className="w-full max-w-md">
        <Card className="p-7 border border-[var(--divider)] shadow-sm">
          <h1 className="font-serif-display text-3xl text-[var(--ink-navy)]">Sign in</h1>
          <p className="text-sm text-slate-600 mt-1">Access your workspace</p>
          {lockout && (
            <Alert
              className="mt-4 border-amber-200 bg-amber-50 text-amber-900"
              data-testid="login-lockout-banner"
              data-lockout-reason={lockout.reason}
            >
              <Info size={16} className="text-amber-600" />
              <AlertDescription className="text-sm text-amber-900">
                <span className="font-medium">{LOCKOUT_TITLES[lockout.reason] || 'Access restricted'}.</span>{' '}
                {lockout.message || LOCKOUT_FALLBACKS[lockout.reason] || 'Please sign in again.'}
              </AlertDescription>
            </Alert>
          )}
          {errorMsg && (
            <Alert
              className="mt-4 border-rose-200 bg-rose-50 text-rose-800"
              data-testid="login-error-alert"
            >
              <ShieldAlert size={16} className="text-rose-600" />
              <AlertDescription className="text-sm text-rose-800">
                {errorMsg}
              </AlertDescription>
            </Alert>
          )}
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-1">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input id="email" type="email" required placeholder="you@company.com"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="pl-9" data-testid="login-email-input" autoComplete="email" />
              </div>
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input id="password" type={show ? 'text' : 'password'} required placeholder="Your password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 pr-9" data-testid="login-password-input" autoComplete="current-password" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="login-toggle-password-visibility" aria-label="Toggle password visibility">
                  {show ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={submitting || loading}
              className="w-full luxury-button mt-2" data-testid="login-form-submit-button">
              {(submitting || loading) ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
          <div className="mt-6 pt-4 border-t border-[var(--divider)] text-[11px] text-slate-500 leading-relaxed">
            <div>Internal portal · Access provided by your Administrator.</div>
            <div className="mt-1 text-slate-400">Forgot password? Contact your Company Admin or Super Admin.</div>
          </div>
        </Card>
      </div>
    </div>
  );
}
